<script setup lang="ts">
import { onClickOutside } from '@vueuse/core';

import { RBN_ACCOUNT_MEDIA, RBN_ACCOUNT_MEDIA_DETAIL, RBN_ACCOUNT_PROFILE, RBN_ACCOUNT_REVIEWS, RBN_FAVORITES, RBN_LOGIN } from '@/utils/constants';

// icons
import { faTimes, faHeart } from '@fortawesome/pro-regular-svg-icons';
import { faCommentAltLines, faPhotoVideo, faUserCog, faSignOut } from '@fortawesome/pro-solid-svg-icons';

import userPlaceholderImage from '@/assets/svg/user_placeholder.svg?url';
import userPlaceholderImageGray from '@/assets/svg/user_placeholder.grey.svg?url';

/********************
 * COMPOSITIONS      *
 ********************/
const appStore = useAppStore();
const backendStore = useBackendStore();
const getRouteBaseName = useRouteBaseName();
const localePath = useLocalePath();
const route = useRoute();
const userStore = useUserStore();
const viewport = useViewport();
const { $gettext, $pgettext, interpolate } = useGettext();

/********************
 * PROPS & EMITS    *
 ********************/
const props = defineProps({
  lightTheme: { type: Boolean, default: false },
  size: { type: String, default: 'md' },
  withMenu: { type: Boolean, default: true },
});

/********************
 * REFS & VARS       *
 ********************/
const userMenu = useTemplateRef('userMenuRef');
onClickOutside(userMenu, onOutsideClicked);

const isMobile = computed(() => {
  return viewport.isLessThan('lg');
});

const userImage = computed(() => {
  return userStore.user?.profile?.images?.small
    ? fixImageUrl(backendStore.url, userStore.user?.profile?.images?.small)
    : '';
});

const placeholderImage = computed(() => {
  return props.lightTheme ? userPlaceholderImage : userPlaceholderImageGray;
});

const i18nUserIconTitle = computed(() => {
  let msg = null;

  if (userStore.loggedIn && userStore.user.profile) {
    msg = $gettext('Logged in');
    const firstName = userStore.user.profile.first_name;
    const lastName = userStore.user.profile.last_name;

    if (firstName && lastName) {
      const msgKnown = $gettext('Logged in as %{name}');
      msg = interpolate(msgKnown, { name: `${firstName} ${lastName}` });
    }
  } else {
    msg = $gettext('Not logged in');
  }

  return msg;
});

const menu = ref<HTMLElement | null>(null);

/********************
 * WATCHER           *
 ********************/
watch(() => route.name, onRouteNameChange, { immediate: true });

/********************
 * FUNCTIONS         *
 ********************/
function onRouteNameChange() {
  appStore.userMenuOpen = false;
}

function onIconClicked(event: Event) {
  if (!props.withMenu) {
    return;
  }

  if (!appStore.userMenuOpen) {
    event.stopPropagation();
  }
  if (userStore.loggedIn) {
    appStore.userMenuOpen = true;
  } else {
    return navigateTo(
      localePath({
        name: RBN_LOGIN,
        query: { redirect: route.fullPath },
      }),
    );
  }
}

async function onLogoutClicked() {
  await userStore.logout();

  const baseName = getRouteBaseName(route);
  if (!baseName) {
    return reloadNuxtApp();
  }

  const redirectPages = new Set([
    'account',
    'account-change-mail',
    'account-change-password',
    RBN_ACCOUNT_MEDIA,
    RBN_ACCOUNT_MEDIA_DETAIL,
    RBN_ACCOUNT_PROFILE,
    RBN_ACCOUNT_REVIEWS,
  ]);

  return redirectPages.has(baseName)
    ? navigateTo(localePath({ name: 'index' }))
    : reloadNuxtApp();
}

function onOutsideClicked() {
  if (appStore.userMenuOpen) {
    appStore.userMenuOpen = false;
  }
}

function handleMenuKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    appStore.userMenuOpen = false;
    return;
  }

  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    event.preventDefault();
    const items = Array.from(menu.value?.querySelectorAll<HTMLElement>('.nav-link') || []);
    const activeElement = document.activeElement;
    const activeIndex = items.findIndex(item => item === activeElement);

    if (activeIndex === -1) {
      items[0]?.focus();
      return;
    }

    let newIndex = activeIndex + (event.key === 'ArrowDown' ? 1 : -1);
    if (newIndex < 0) {
      newIndex = items.length - 1;
    }
    if (newIndex >= items.length) {
      newIndex = 0;
    }

    items[newIndex]?.focus();
  }
}
</script>

<template>
  <div class="relative">
    <client-only>
      <div
        :title="i18nUserIconTitle"
        class="user-icon relative cursor-pointer"
        :class="`user-icon--${size}`"
        :data-testid="`ci-user-icon--user-menu-${props.withMenu}`"
        :aria-haspopup="userStore.loggedIn ? 'menu' : false"
        @click="onIconClicked"
      >
        <!-- user icon -->
        <img
          v-if="userStore.loggedIn && userImage"
          :src="userImage"
          :alt="i18nUserIconTitle"
          class="user-icon__image relative block rounded-full bg-cover bg-center"
          width="52"
          height="52"
          loading="lazy"
        />
        <img
          v-else
          :src="placeholderImage"
          alt="user image placeholder"
          class="user-icon__image relative block rounded-full bg-cover bg-center"
          width="52"
          height="52"
          loading="lazy"
        />
        <div
          class="user-icon__indicator absolute border border-white"
          :class="[userStore.loggedIn ? 'bg-primary' : 'bg-danger']"
        />
      </div>
      <template #placeholder>
        <img
          class="user-icon__image relative block rounded-full bg-cover bg-center"
          :src="placeholderImage"
          alt="user image"
          width="52"
          height="52"
        />
      </template>
    </client-only>
    <transition name="user-menu-slide-fade">
      <div
        v-if="appStore.userMenuOpen && props.withMenu"
        ref="menu"
        role="menu"
        aria-orientation="vertical"
        class="user-icon__menu-wrapper fixed right-0 top-0 z-[988] h-screen max-h-screen w-[17.5rem] max-w-[80vw] overflow-auto rounded-none bg-white text-primary shadow-[0_0_1rem_rgba(0,0,0,0.5)] lg:absolute lg:h-auto lg:max-h-[calc(100vh-90px)] lg:w-[250px] lg:rounded-[5px]"
        @keydown="handleMenuKeydown"
      >
        <div ref="userMenuRef" class="user-icon__menu h-100vh lg:h-auto">
          <div class="border-b-[3px] border-primary bg-dark-green">
            <client-only>
              <div v-if="userStore.user.profile?.first_name" class="flex items-center p-4 text-white">
                <span class="mr-3 truncate"><span class="mr-1">{{ $gettext('Hi') }}</span>{{
                  userStore.user.profile.first_name }}</span>
              </div>
            </client-only>
          </div>

          <ul class="m-0 list-none p-0">
            <li role="menuitem" class="nav-item">
              <nuxt-link
                :to="localePath({ name: RBN_ACCOUNT_REVIEWS })"
                class="nav-link flex items-center p-4 hover:no-underline lg:px-4 lg:py-2"
              >
                <CiAwesomeIcon class="fill-dark-green" :icon="faCommentAltLines" :ratio="isMobile ? '0.9' : '0.7'" />
                <span class="ml-4 lg:ml-2">{{ $pgettext('Account navigation reviews', 'Reviews') }}</span>
              </nuxt-link>
            </li>
            <li role="menuitem" class="nav-item">
              <nuxt-link
                :to="localePath({ name: RBN_ACCOUNT_MEDIA })"
                class="nav-link flex items-center p-4 hover:no-underline lg:px-4 lg:py-2"
              >
                <CiAwesomeIcon class="fill-dark-green" :icon="faPhotoVideo" :ratio="isMobile ? '0.9' : '0.7'" />
                <span class="ml-4 lg:ml-2">{{
                  $pgettext('Account navigation pictures & videos', 'Pictures & Videos')
                }}</span>
              </nuxt-link>
            </li>
            <li role="menuitem" class="nav-item">
              <nuxt-link
                :to="localePath({ name: RBN_FAVORITES })"
                class="nav-link flex items-center p-4 hover:no-underline lg:px-4 lg:py-2"
              >
                <CiAwesomeIcon class="fill-dark-green" :icon="faHeart" :ratio="isMobile ? '0.9' : '0.7'" />
                <span class="ml-4 lg:ml-2">{{ $pgettext('Header Navigation Title', 'Bookmarks') }}</span>
              </nuxt-link>
            </li>
            <li role="menuitem" class="nav-item">
              <nuxt-link
                :to="localePath({ name: RBN_ACCOUNT_PROFILE })"
                class="nav-link flex items-center p-4 hover:no-underline lg:px-4 lg:py-2"
              >
                <CiAwesomeIcon class="fill-dark-green" :icon="faUserCog" :ratio="isMobile ? '0.9' : '0.7'" />
                <span class="ml-4 lg:ml-2">{{ $pgettext('Account navigation profile', 'Profile') }}</span>
              </nuxt-link>
            </li>
          </ul>

          <ul class="m-0 list-none border-t border-gray-20 p-0">
            <li class="nav-item">
              <CiButton
                tag="button"
                variant="text"
                class="nav-link flex w-full items-center justify-start rounded-none p-4 text-danger hover:no-underline lg:px-4 lg:py-2"
                data-testid="ci-user-icon--logout"
                @click="onLogoutClicked"
              >
                <CiAwesomeIcon class="mr-4 fill-dark-green lg:mr-2" :icon="faSignOut" :ratio="isMobile ? '0.9' : '0.7'" />
                {{ $pgettext('Header Navigation Title', 'Logout') }}
              </CiButton>
            </li>
          </ul>
        </div>

        <CiButton
          v-if="appStore.userMenuOpen"
          tag="button"
          variant="text"
          class="fixed right-[min(80vw,280px)] top-0 block lg:hidden"
          :aria-label="$gettext('Close')"
          size="icon"
          data-cy="ci-user-menu-close"
          @click="appStore.userMenuOpen = false"
        >
          <CiAwesomeIcon class="fill-white" :icon="faTimes" ratio="1.2" />
        </CiButton>
      </div>
    </transition>
  </div>
</template>

<style lang="postcss" scoped>
.user-icon {
  .user-icon__image {
    @apply w-8;
  }

  .user-icon__indicator {
    @apply right-0 top-0 size-[10px] rounded-[5px];
  }

  &.user-icon--sm {
    .user-icon__image {
      width: 1.5rem;
      height: 1.5rem;
    }

    .user-icon__indicator {
      width: 6px;
      height: 6px;
    }
  }

  &.user-icon--md {
    .user-icon__image {
      width: 2rem;
      height: 2rem;
    }
  }

  &.user-icon--lg {
    .user-icon__image {
      height: 3.25rem;
      width: 3.25rem;
    }

    .user-icon__indicator {
      border-radius: 6px;
      height: 12px;
      right: 2px;
      top: 2px;
      width: 12px;
    }
  }

  /* User icon menu */
  &__menu {
    -webkit-overflow-scrolling: touch;
  }
}

  @screen lg {
    .user-icon__menu {
      .nav-link {
        .no-touch &:hover {
          @apply bg-gray-20;
        }
      }
    }

    .user-icon.user-icon--sm + .user-icon__menu-wrapper {
      top: 5.6875rem;
    }

    .user-icon.user-icon--lg + .user-icon__menu-wrapper {
      top: 4.0625rem;
    }
  }

/* user menu transitions */
.user-menu-slide-fade-enter-active,
.user-menu-slide-fade-leave-active {
  transition: transform 0.4s ease-out;
}

.user-menu-slide-fade-enter-from,
.user-menu-slide-fade-leave-to {
  transform: translateX(90vw);
}

@screen lg {
  .user-menu-slide-fade-enter-active,
  .user-menu-slide-fade-leave-active {
    transform: translateY(0);
    transition: opacity 0.15s ease, transform 0.15s ease;
  }

  .user-menu-slide-fade-enter-from,
  .user-menu-slide-fade-leave-to {
    transition:
      opacity 0.15s ease,
      transform 0.15s ease;
    transform: translateY(-1rem);
    opacity: 0;
  }
}
</style>
